import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MonitorPlay, LogOut, Users, CheckCircle2, Clock, Loader2, Trash2, Plus, Power, KeyRound, X, CreditCard, Save, Settings2, ListVideo, RefreshCw, ScrollText, History, Radio } from "lucide-react";

export default function AdminDashboard() {
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState({});
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [q, setQ] = useState("");
  const [mgr, setMgr] = useState(null);
  const [logs, setLogs] = useState(null);
  const [pstats, setPstats] = useState(null);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [d, s, p, cfg, ps] = await Promise.all([
        api.get("/admin/devices"), api.get("/admin/stats"),
        api.get("/admin/payments"), api.get("/admin/settings"),
        api.get("/admin/playback-stats").catch(() => ({ data: null })),
      ]);
      setDevices(d.data); setStats(s.data); setPayments(p.data);
      setPlans(cfg.data.plans || []); setSettings(cfg.data); setPstats(ps.data);
    } catch { navigate("/admin"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin"); return; }
    load();
    const pollActive = () => api.get("/admin/active-streams").then((r) => setActive(r.data)).catch(() => {});
    pollActive();
    const iv = setInterval(pollActive, 10000);
    return () => clearInterval(iv);
  }, []);

  const activate = async (mac, months) => {
    await api.post(`/admin/devices/${mac}/activate`, { months });
    toast.success(months === 0 ? "Device activated (Lifetime)" : `Device activated for ${months} month(s)`);
    load();
  };
  const deactivate = async (mac) => { await api.post(`/admin/devices/${mac}/deactivate`); toast.success("Disabled"); load(); };
  const del = async (mac) => { await api.delete(`/admin/devices/${mac}`); toast.success("Deleted"); load(); };
  const confirmPay = async (id) => { await api.post(`/admin/payments/${id}/confirm`); toast.success("Payment confirmed & device activated"); load(); };
  const rejectPay = async (id) => { await api.post(`/admin/payments/${id}/reject`); toast.success("Rejected"); load(); };

  const logout = () => { localStorage.removeItem("admin_token"); navigate("/admin"); };

  const term = q.trim().toLowerCase();
  const filtered = !term ? devices : devices.filter((d) =>
    d.mac.toLowerCase().includes(term) ||
    (d.device_key || "").toLowerCase().includes(term) ||
    (d.name || "").toLowerCase().includes(term) ||
    (d.playlists || []).some((p) => (p.name || "").toLowerCase().includes(term)));
  const rel = (iso) => { if (!iso) return "Never"; const s = (Date.now() - new Date(iso)) / 1000; if (s < 120) return "Just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return new Date(iso).toLocaleDateString("en-GB"); };
  const editName = async (d) => { const n = window.prompt("Device name", d.name || ""); if (n === null) return; await api.post(`/admin/devices/${d.mac}/name`, { name: n }); toast.success("Name saved"); load(); };

  const onlineNow = devices.filter((d) => d.online).length;
  const STAT = [
    { label: "Total devices", value: stats.total, icon: Users, color: "text-zinc-100" },
    { label: "Online now", value: onlineNow, icon: CheckCircle2, color: "text-emerald-400" },
    { label: "Offline", value: Math.max(0, (stats.total || devices.length) - onlineNow), icon: X, color: "text-zinc-500" },
    { label: "Active", value: stats.active, icon: CheckCircle2, color: "text-emerald-400" },
    { label: "Paid", value: stats.paid, icon: CheckCircle2, color: "text-red-500" },
    { label: "Trial", value: stats.trial, icon: Clock, color: "text-amber-400" },
    { label: "Expired", value: stats.expired, icon: X, color: "text-zinc-400" },
    { label: "Pending", value: stats.pending_payments, icon: CreditCard, color: "text-sky-400" },
  ];

  return (
    <div className="min-h-screen">
      <header className="flex items-center justify-between px-6 md:px-10 h-16 border-b border-zinc-800/50">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-red-600"><MonitorPlay className="w-4 h-4 text-white" /></div>
          <span className="font-head font-bold">Admin Panel</span>
        </div>
        <div className="flex items-center gap-4">
          <button data-testid="change-password-btn" onClick={() => setShowPwd(true)} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <KeyRound className="w-4 h-4" /> Change password
          </button>
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
      {mgr && <DevicePlaylistModal mac={mgr} onClose={() => setMgr(null)} onChanged={load} />}
      {logs && <DeviceLogsModal mac={logs} onClose={() => setLogs(null)} />}

      <div className="p-6 md:p-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {STAT.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="p-5 rounded-xl bg-[#121217] border border-zinc-800/50">
              <Icon className={`w-5 h-5 ${color}`} />
              <p className={`font-head text-3xl font-bold mt-3 ${color}`}>{value ?? 0}</p>
              <p className="text-zinc-500 text-sm">{label}</p>
            </div>
          ))}
        </div>

        {(active || (pstats && (pstats.total_direct || pstats.total_proxied))) ? (
          <div className="mb-8 p-5 rounded-xl bg-[#121217] border border-zinc-800/50" data-testid="streaming-diagnostics">
            <div className="flex items-center gap-2 mb-3"><Radio className="w-5 h-5 text-red-500" /><h3 className="font-head text-lg font-semibold">Streaming diagnostics</h3></div>

            {active && (
              <div className="mb-5">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Active now</p>
                <div className="grid grid-cols-3 gap-4">
                  <div><p className="font-head text-3xl font-bold text-emerald-400" data-testid="active-direct">{active.direct}</p><p className="text-zinc-500 text-sm">Direct streams</p></div>
                  <div><p className="font-head text-3xl font-bold text-amber-400" data-testid="active-proxied">{active.proxied}</p><p className="text-zinc-500 text-sm">Proxied streams</p></div>
                  <div><p className="font-head text-3xl font-bold text-zinc-100" data-testid="active-total">{active.total}</p><p className="text-zinc-500 text-sm">Total streams</p></div>
                </div>
                <p className="text-xs text-zinc-600 mt-2" data-testid="active-peaks">Peak today — proxied {active.peak_proxied} · total {active.peak_total}</p>
              </div>
            )}

            {pstats && (pstats.total_direct || pstats.total_proxied) ? (<>
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">All-time totals</p>
              <p className="text-zinc-500 text-sm mb-3">Direct plays go straight from the IPTV provider to the device (0 bandwidth on this server). Proxied plays pass through this server.</p>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div><p className="font-head text-2xl font-bold text-emerald-400" data-testid="stat-direct">{pstats.total_direct.toLocaleString()}</p><p className="text-zinc-500 text-sm">Direct plays</p></div>
                <div><p className="font-head text-2xl font-bold text-amber-400" data-testid="stat-proxied">{pstats.total_proxied.toLocaleString()}</p><p className="text-zinc-500 text-sm">Proxied plays</p></div>
                <div><p className="font-head text-2xl font-bold text-zinc-100">{pstats.proxied_pct}%</p><p className="text-zinc-500 text-sm">Through this server</p></div>
              </div>
              <div className="h-2 rounded-full bg-emerald-500/70 overflow-hidden flex">
                <div className="h-full bg-emerald-500" style={{ width: `${100 - pstats.proxied_pct}%` }} />
                <div className="h-full bg-amber-500" style={{ width: `${pstats.proxied_pct}%` }} />
              </div>
            </>) : null}
          </div>
        ) : null}

        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <h2 className="font-head text-xl font-semibold">Devices</h2>
          <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by MAC, Device Key or playlist name…"
            className="w-72 max-w-full px-4 py-2 rounded-full bg-[#121217] border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50" />
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-red-500 animate-spin" /></div>
        ) : (
          <div className="rounded-xl border border-zinc-800/50 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0a0a0f] text-zinc-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">MAC / Key</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Playlist</th>
                  <th className="px-4 py-3 font-semibold">Valid until</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.mac} data-testid={`device-row-${d.mac}`} className="border-t border-zinc-800/50 hover:bg-zinc-900/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${d.online ? "bg-emerald-400" : "bg-zinc-600"}`} title={d.online ? "Online" : "Offline"} />
                        <button data-testid={`device-name-${d.mac}`} onClick={() => editName(d)} className="font-medium text-zinc-100 hover:text-red-400 truncate max-w-[160px]">{d.name || "Unnamed device"}</button>
                      </div>
                      <code className="font-mono-data text-zinc-300 text-xs">{d.mac}</code>
                      <div className="text-xs text-zinc-600 font-mono-data">{d.device_key} · seen {rel(d.last_seen)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 ${d.active ? "text-emerald-400" : "text-red-500"}`}>
                        <span className={`w-2 h-2 rounded-full ${d.active ? "bg-emerald-400" : "bg-red-500"}`} />
                        {d.status === "active" ? "Active" : d.active ? `Trial (${d.days_left}d)` : "Expired"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`manage-playlist-${d.mac}`} onClick={() => setMgr(d.mac)} className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white">
                        <ListVideo className="w-4 h-4 text-red-500" />
                        {d.playlists?.[0]?.name || "No playlist"}
                        {d.playlists?.length > 1 && <span className="text-xs text-zinc-500">+{d.playlists.length - 1}</span>}
                        {d.has_privacy && <span title="User set a privacy password" className="text-amber-400"><KeyRound className="w-3 h-3" /></span>}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">{new Date(d.expiry).toLocaleDateString("en-GB")}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {[1, 3, 12].map((m) => (
                          <button key={m} data-testid={`activate-${d.mac}-${m}`} onClick={() => activate(d.mac, m)}
                            className="px-2.5 py-1 rounded-md bg-zinc-800 hover:bg-red-600 text-xs font-semibold transition-colors">
                            +{m}m
                          </button>
                        ))}
                        <button data-testid={`activate-${d.mac}-life`} onClick={() => activate(d.mac, 0)} title="Lifetime"
                          className="px-2.5 py-1 rounded-md bg-zinc-800 hover:bg-red-600 text-xs font-semibold transition-colors">Life</button>
                        <button data-testid={`logs-${d.mac}`} onClick={() => setLogs(d.mac)} title="View logs" className="p-1.5 rounded-md bg-zinc-800 hover:bg-sky-600 transition-colors"><ScrollText className="w-3.5 h-3.5" /></button>
                        <button data-testid={`deactivate-${d.mac}`} onClick={() => deactivate(d.mac)} title="Disable" className="p-1.5 rounded-md bg-zinc-800 hover:bg-amber-600 transition-colors"><Power className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-${d.mac}`} onClick={() => del(d.mac)} title="Delete" className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-700 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {devices.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500">No registered devices</td></tr>}
                {devices.length > 0 && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-zinc-500">No devices match “{q}”</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <PendingPanel payments={payments} onConfirm={confirmPay} onReject={rejectPay} />
        <PlansSettingsPanel plans={plans} settings={settings} onReload={load} />
      </div>
    </div>
  );
}

function PendingPanel({ payments, onConfirm, onReject }) {
  const pending = payments.filter((p) => p.status === "pending");
  const history = payments.filter((p) => p.status !== "pending").slice(0, 20);
  return (
    <div className="mt-10">
      <h2 className="font-head text-xl font-semibold mb-4">Pending Activation Requests</h2>
      <div className="rounded-xl border border-zinc-800/50 overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0f] text-zinc-500 text-left"><tr>
            <th className="px-4 py-3">Device</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3 text-right">Action</th>
          </tr></thead>
          <tbody>
            {pending.map((p) => (
              <tr key={p.id} data-testid={`payment-${p.id}`} className="border-t border-zinc-800/50">
                <td className="px-4 py-3 font-mono-data text-zinc-200">{p.mac}</td>
                <td className="px-4 py-3">{p.plan}</td>
                <td className="px-4 py-3">{p.amount} {p.currency}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`confirm-${p.id}`} onClick={() => onConfirm(p.id)} className="px-3 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold">Confirm & Activate</button>
                    <button data-testid={`reject-${p.id}`} onClick={() => onReject(p.id)} className="px-3 py-1 rounded-md bg-zinc-800 hover:bg-red-700 text-xs font-semibold">Reject</button>
                  </div>
                </td>
              </tr>
            ))}
            {pending.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500">No pending requests</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="font-head text-xl font-semibold mb-4">Payment / Activation History</h2>
      <div className="rounded-xl border border-zinc-800/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0f] text-zinc-500 text-left"><tr>
            <th className="px-4 py-3">Device</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Expiry</th>
          </tr></thead>
          <tbody>
            {history.map((p) => (
              <tr key={p.id} className="border-t border-zinc-800/50">
                <td className="px-4 py-3 font-mono-data text-zinc-300">{p.mac}</td>
                <td className="px-4 py-3">{p.plan}</td>
                <td className="px-4 py-3">{p.amount} {p.currency}</td>
                <td className="px-4 py-3">{p.method}</td>
                <td className="px-4 py-3"><span className={p.status === "paid" ? "text-emerald-400" : "text-red-400"}>{p.status}</span></td>
                <td className="px-4 py-3 text-zinc-400">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                <td className="px-4 py-3 text-zinc-400">{p.expiry ? new Date(p.expiry).toLocaleDateString("en-GB") : "—"}</td>
              </tr>
            ))}
            {history.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No history yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlansSettingsPanel({ plans, settings, onReload }) {
  const [local, setLocal] = useState(plans);
  const [cfg, setCfg] = useState(settings || {});
  useEffect(() => { setLocal(plans); }, [plans]);
  useEffect(() => { setCfg(settings || {}); }, [settings]);

  const savePlan = async (p) => { await api.put(`/admin/plans/${p.id}`, { name: p.name, months: Number(p.months), price: Number(p.price), currency: p.currency || cfg.currency || "EUR", order: Number(p.order || 0), active: p.active }); toast.success("Plan saved"); onReload(); };
  const addPlan = async () => { await api.post(`/admin/plans`, { name: "New Plan", months: 12, price: 10, currency: cfg.currency || "EUR", order: (local.length || 0), active: true }); onReload(); };
  const delPlan = async (id) => { await api.delete(`/admin/plans/${id}`); onReload(); };
  const saveCfg = async () => { await api.put(`/admin/settings`, { apk_url: cfg.apk_url, play_store_url: cfg.play_store_url, currency: cfg.currency, trial_days: Number(cfg.trial_days), download_instructions: cfg.download_instructions, update_version_name: cfg.update_version_name, update_version_code: Number(cfg.update_version_code || 0), update_apk_url: cfg.update_apk_url, update_message: cfg.update_message, update_force: !!cfg.update_force, manage_url: cfg.manage_url || "", payment_url: cfg.payment_url || "", support_url: cfg.support_url || "" }); toast.success("Settings saved"); onReload(); };

  const upd = (i, k, v) => setLocal((a) => a.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const inp = "px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50";

  return (
    <div className="mt-10 grid lg:grid-cols-2 gap-6">
      <div className="p-6 rounded-xl bg-[#121217] border border-zinc-800/50">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-red-500" /><h3 className="font-head text-lg font-semibold">Activation Plans</h3></div>
          <button data-testid="add-plan-btn" onClick={addPlan} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 hover:bg-red-500 text-xs font-semibold"><Plus className="w-3.5 h-3.5" /> Add plan</button>
        </div>
        <div className="space-y-3">
          {local.map((p, i) => (
            <div key={p.id} data-testid={`admin-plan-${p.id}`} className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-zinc-900 border border-zinc-800/50">
              <input className={`${inp} w-32`} value={p.name} onChange={(e) => upd(i, "name", e.target.value)} />
              <input className={`${inp} w-20`} type="number" value={p.months} onChange={(e) => upd(i, "months", e.target.value)} title="Months (0=Lifetime)" />
              <input className={`${inp} w-20`} type="number" value={p.price} onChange={(e) => upd(i, "price", e.target.value)} title="Price" />
              <input className={`${inp} w-16`} value={p.currency ?? "EUR"} onChange={(e) => upd(i, "currency", e.target.value)} title="Currency" />
              <input className={`${inp} w-14`} type="number" value={p.order ?? 0} onChange={(e) => upd(i, "order", e.target.value)} title="Display order" />
              <label className="flex items-center gap-1 text-xs text-zinc-400" title="Lifetime = set Months to 0"><input type="checkbox" checked={Number(p.months) === 0} onChange={(e) => upd(i, "months", e.target.checked ? 0 : 12)} /> lifetime</label>
              <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={p.active} onChange={(e) => upd(i, "active", e.target.checked)} /> active</label>
              <button data-testid={`save-plan-${p.id}`} onClick={() => savePlan(p)} className="ml-auto p-1.5 rounded-md bg-zinc-800 hover:bg-emerald-600"><Save className="w-3.5 h-3.5" /></button>
              <button onClick={() => delPlan(p.id)} className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 rounded-xl bg-[#121217] border border-zinc-800/50">
        <div className="flex items-center gap-2 mb-4"><Settings2 className="w-5 h-5 text-red-500" /><h3 className="font-head text-lg font-semibold">App & Download Settings</h3></div>
        <div className="space-y-3">
          <div><label className="text-xs text-zinc-500">APK URL (hosted on your domain)</label><input data-testid="apk-url-input" className={`${inp} w-full mt-1`} value={cfg.apk_url || ""} onChange={(e) => setCfg({ ...cfg, apk_url: e.target.value })} placeholder="https://yourdomain.com/ibostream.apk" /></div>
          <div><label className="text-xs text-zinc-500">Google Play URL</label><input data-testid="play-url-input" className={`${inp} w-full mt-1`} value={cfg.play_store_url || ""} onChange={(e) => setCfg({ ...cfg, play_store_url: e.target.value })} placeholder="https://play.google.com/store/apps/details?id=..." /></div>
          <div className="flex gap-3">
            <div className="flex-1"><label className="text-xs text-zinc-500">Currency</label><input className={`${inp} w-full mt-1`} value={cfg.currency || ""} onChange={(e) => setCfg({ ...cfg, currency: e.target.value })} /></div>
            <div className="flex-1"><label className="text-xs text-zinc-500">Trial days</label><input className={`${inp} w-full mt-1`} type="number" value={cfg.trial_days ?? 7} onChange={(e) => setCfg({ ...cfg, trial_days: e.target.value })} /></div>
          </div>
          <div><label className="text-xs text-zinc-500">Download instructions (one step per line)</label><textarea className={`${inp} w-full mt-1 h-24`} value={cfg.download_instructions || ""} onChange={(e) => setCfg({ ...cfg, download_instructions: e.target.value })} /></div>

          <div className="pt-3 mt-1 border-t border-zinc-800/60">
            <p className="text-sm font-semibold mb-1">Public device links (shown in the TV app + QR)</p>
            <p className="text-xs text-zinc-500 mb-3">Loaded live by the app — editable any time, no APK rebuild. Leave empty to hide that button.</p>
            <div><label className="text-xs text-zinc-500">Manage / Add-playlist URL</label><input data-testid="manage-url-input" className={`${inp} w-full mt-1`} value={cfg.manage_url || ""} onChange={(e) => setCfg({ ...cfg, manage_url: e.target.value })} placeholder="https://tv.yourdomain.com/manage" /></div>
            <div className="mt-3"><label className="text-xs text-zinc-500">Payment / Activate URL</label><input data-testid="payment-url-input" className={`${inp} w-full mt-1`} value={cfg.payment_url || ""} onChange={(e) => setCfg({ ...cfg, payment_url: e.target.value })} placeholder="https://tv.yourdomain.com/manage (or your checkout URL)" /></div>
            <div className="mt-3"><label className="text-xs text-zinc-500">Support URL (optional)</label><input data-testid="support-url-input" className={`${inp} w-full mt-1`} value={cfg.support_url || ""} onChange={(e) => setCfg({ ...cfg, support_url: e.target.value })} placeholder="https://t.me/yoursupport" /></div>
          </div>

          <div className="pt-3 mt-1 border-t border-zinc-800/60">
            <p className="text-sm font-semibold mb-3">Android App Update</p>
            <div className="flex gap-3">
              <div className="flex-1"><label className="text-xs text-zinc-500">Latest version name</label><input data-testid="upd-name-input" className={`${inp} w-full mt-1`} value={cfg.update_version_name || ""} onChange={(e) => setCfg({ ...cfg, update_version_name: e.target.value })} placeholder="1.2.0" /></div>
              <div className="w-32"><label className="text-xs text-zinc-500">Version code</label><input data-testid="upd-code-input" type="number" className={`${inp} w-full mt-1`} value={cfg.update_version_code ?? 0} onChange={(e) => setCfg({ ...cfg, update_version_code: e.target.value })} placeholder="2" /></div>
            </div>
            <div className="mt-3"><label className="text-xs text-zinc-500">Update APK URL</label><input data-testid="upd-apk-input" className={`${inp} w-full mt-1`} value={cfg.update_apk_url || ""} onChange={(e) => setCfg({ ...cfg, update_apk_url: e.target.value })} placeholder="https://yourdomain.com/ibostream-1.2.0.apk" /></div>
            <div className="mt-3"><label className="text-xs text-zinc-500">Update message</label><input data-testid="upd-msg-input" className={`${inp} w-full mt-1`} value={cfg.update_message || ""} onChange={(e) => setCfg({ ...cfg, update_message: e.target.value })} placeholder="What's new…" /></div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 mt-3"><input data-testid="upd-force-input" type="checkbox" checked={!!cfg.update_force} onChange={(e) => setCfg({ ...cfg, update_force: e.target.checked })} /> Force update (block app until updated)</label>
          </div>

          <button data-testid="save-settings-btn" onClick={saveCfg} className="w-full py-2.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-semibold">Save settings</button>
        </div>
      </div>
    </div>
  );
}


function DeviceLogsModal({ mac, onClose }) {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const inp = "px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50";
  const load = async () => {
    setRows(null);
    const { data } = await api.get(`/admin/devices/${mac}/logs`, { params: { type, date_from: from || undefined, date_to: to || undefined } });
    setRows(data);
  };
  useEffect(() => { load(); }, [mac, type, from, to]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-2xl p-7 space-y-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="device-logs-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5"><div className="p-2 rounded-lg bg-sky-600"><ScrollText className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-head text-lg font-semibold">Device Logs</h3><code className="text-xs text-zinc-500 font-mono-data">{mac}</code></div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-zinc-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="logs-type" className={inp} value={type} onChange={(e) => setType(e.target.value)}>
            {["all", "app_opened", "playlist_loaded", "playback_started", "playback_error", "reconnect_attempt", "reconnect_success", "reconnect_failed"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input data-testid="logs-from" type="date" className={inp} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input data-testid="logs-to" type="date" className={inp} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {rows === null ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-sky-500 animate-spin" /></div>
            : rows.length === 0 ? <p className="text-zinc-500 text-sm py-8 text-center">No log entries.</p>
            : rows.map((r, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-zinc-900 border border-zinc-800/50 text-sm">
                <span className="font-mono-data text-xs text-zinc-500 w-36 shrink-0">{new Date(r.at).toLocaleString("en-GB")}</span>
                <span className="px-2 py-0.5 rounded bg-zinc-800 text-xs font-semibold shrink-0">{r.type}</span>
                <span className="text-zinc-300 flex-1 break-all">{r.message}{r.app_version ? ` · v${r.app_version}` : ""}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}


function DevicePlaylistModal({ mac, onClose, onChanged }) {
  const [dev, setDev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState("m3u");
  const [f, setF] = useState({ name: "My Playlist", url: "", username: "", password: "" });
  const [job, setJob] = useState(null);
  const [dbg, setDbg] = useState(null);
  const [apin, setApin] = useState("");
  const pollRef = useRef(null);
  const inp = "px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm w-full focus:outline-none focus:ring-2 focus:ring-red-500/50";

  const load = async () => { const { data } = await api.get(`/admin/devices/${mac}`); setDev(data); };
  useEffect(() => { load(); return () => { if (pollRef.current) clearTimeout(pollRef.current); }; }, [mac]);

  const poll = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/import/${jobId}`);
      setJob(data);
      if (data.status === "done") {
        if (pollRef.current) clearTimeout(pollRef.current);
        setBusy(false); setJob(null);
        toast.success("Playlist saved — it will appear in the TV app");
        setF({ name: "My Playlist", url: "", username: "", password: "" });
        await load(); onChanged && onChanged();
        return;
      }
      if (data.status === "error") {
        if (pollRef.current) clearTimeout(pollRef.current);
        setBusy(false); setJob(null);
        toast.error(data.error || "Could not load the playlist");
        return;
      }
    } catch (e) { /* transient */ }
    pollRef.current = setTimeout(() => poll(jobId), 700);
  };

  const add = async () => {
    setBusy(true);
    setJob({ status: "queued", stage: "Starting…", pct: 0, current: 0, total: 0 });
    try {
      const { data } = await api.post(`/admin/devices/${mac}/playlist/import`, { name: f.name, type, url: f.url, username: f.username, password: f.password });
      poll(data.job_id);
    } catch (e) { setBusy(false); setJob(null); toast.error(e.response?.data?.detail || "Could not load the playlist"); }
  };
  const refresh = async (pid) => { setBusy(true); try { await api.post(`/admin/devices/${mac}/playlist/${pid}/refresh`); toast.success("Playlist reloaded"); await load(); onChanged && onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); } finally { setBusy(false); } };
  const remove = async (pid) => { setBusy(true); try { await api.delete(`/admin/devices/${mac}/playlist/${pid}`); toast.success("Playlist removed"); await load(); onChanged && onChanged(); } finally { setBusy(false); } };
  const setPin = async () => {
    if (!/^\d{4}$/.test(apin)) { toast.error("PIN must be 4 digits"); return; }
    setBusy(true);
    try { await api.post(`/admin/devices/${mac}/pin`, { pin: apin }); toast.success("Adult PIN set"); setApin(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const removePin = async () => {
    setBusy(true);
    try { await api.delete(`/admin/devices/${mac}/pin`); toast.success("Adult PIN removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const pct = Math.max(0, Math.min(100, job?.pct || 0));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      {dbg && <CatchupDebugModal mac={mac} pid={dbg} onClose={() => setDbg(null)} />}
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-2xl p-7 space-y-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="device-playlist-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5"><div className="p-2 rounded-lg bg-red-600"><ListVideo className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-head text-lg font-semibold">Playlists</h3><code className="text-xs text-zinc-500 font-mono-data">{mac}</code></div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-zinc-800"><X className="w-4 h-4" /></button>
        </div>

        {!dev ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-red-500 animate-spin" /></div> : (
          <>
            <div className="space-y-3">
              {(dev.playlists || []).map((p) => (
                <div key={p.id} data-testid={`admin-pl-${p.id}`} className="p-4 rounded-lg bg-zinc-900 border border-zinc-800/50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{p.name} <span className="text-xs text-zinc-500 uppercase">· {p.type}</span></p>
                      <p className="text-xs text-zinc-500">TV {p.counts?.live || 0} · Movies {p.counts?.movie || 0} · Series {p.counts?.series || 0} · Updated {p.added_at ? new Date(p.added_at).toLocaleDateString("en-GB") : "—"}</p>
                    </div>
                    <button data-testid={`admin-pl-catchup-${p.id}`} disabled={busy} onClick={() => setDbg(p.id)} title="Catch-Up diagnostics" className="p-1.5 rounded-md bg-zinc-800 hover:bg-amber-600"><History className="w-3.5 h-3.5" /></button>
                    <button data-testid={`admin-pl-refresh-${p.id}`} disabled={busy} onClick={() => refresh(p.id)} className="p-1.5 rounded-md bg-zinc-800 hover:bg-sky-600"><RefreshCw className="w-3.5 h-3.5" /></button>
                    <button data-testid={`admin-pl-delete-${p.id}`} disabled={busy} onClick={() => remove(p.id)} className="p-1.5 rounded-md bg-zinc-800 hover:bg-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {p.source && (
                    <div className="mt-3 pt-3 border-t border-zinc-800/60 text-xs font-mono-data space-y-1 text-zinc-400 break-all">
                      {p.type === "xtream" ? (<>
                        <div><span className="text-zinc-600">Server: </span>{p.source.url}</div>
                        <div><span className="text-zinc-600">Username: </span>{p.source.username}</div>
                        <div><span className="text-zinc-600">Password: </span>{p.source.password}</div>
                      </>) : (<div><span className="text-zinc-600">M3U URL: </span>{p.source.url}</div>)}
                    </div>
                  )}
                </div>
              ))}
              {(dev.playlists || []).length === 0 && <p className="text-zinc-500 text-sm">No playlist assigned yet.</p>}
            </div>

            <div className="pt-2 border-t border-zinc-800/60" data-testid="admin-pin-card">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" /> Adult / Parental PIN</p>
              <p className="text-xs text-zinc-500 mb-3">{dev.has_pin ? "A PIN is currently set on this device." : "No PIN set. Adult categories stay locked until a PIN exists."} Admin can set or reset it without the current PIN.</p>
              <div className="flex flex-wrap items-center gap-2">
                <input data-testid="admin-pin-input" className={`${inp} w-32 text-center tracking-[0.4em] font-mono-data`} maxLength={4} inputMode="numeric" placeholder="4-digit PIN" value={apin} onChange={(e) => setApin(e.target.value.replace(/\D/g, ""))} disabled={busy} />
                <button data-testid="admin-pin-set" disabled={busy} onClick={setPin} className="px-4 py-2 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-semibold">{dev.has_pin ? "Reset PIN" : "Set PIN"}</button>
                {dev.has_pin && <button data-testid="admin-pin-remove" disabled={busy} onClick={removePin} className="px-4 py-2 rounded-full bg-zinc-800 hover:bg-red-700 text-sm font-semibold">Remove PIN</button>}
              </div>
            </div>

            <div className="pt-2 border-t border-zinc-800/60">
              <p className="text-sm font-semibold mb-3">{dev.playlists?.length ? "Add / Replace playlist" : "Add playlist"}</p>
              <div className="flex gap-2 mb-3">
                <button onClick={() => setType("m3u")} disabled={busy} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${type === "m3u" ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>M3U URL</button>
                <button onClick={() => setType("xtream")} disabled={busy} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${type === "xtream" ? "bg-red-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>Xtream Codes</button>
              </div>
              <div className="space-y-2">
                <input data-testid="admin-pl-name" className={inp} placeholder="Playlist name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} disabled={busy} />
                {type === "m3u" ? (
                  <input data-testid="admin-pl-url" className={inp} placeholder="Any playlist URL — .m3u/.m3u8/get.php/playlist/..." value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} disabled={busy} />
                ) : (<>
                  <input data-testid="admin-pl-server" className={inp} placeholder="Server URL (http://host:port)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} disabled={busy} />
                  <div className="flex gap-2">
                    <input data-testid="admin-pl-user" className={inp} placeholder="Username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} disabled={busy} />
                    <input data-testid="admin-pl-pass" className={inp} placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} disabled={busy} />
                  </div>
                </>)}
                {job && busy && (
                  <div data-testid="admin-import-progress" className="rounded-lg bg-zinc-900 border border-zinc-800 p-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span data-testid="admin-import-stage" className="font-semibold text-zinc-200 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-red-500" /> {job.stage || "Working…"}</span>
                      <span className="font-mono-data text-red-400">{pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-red-600 transition-all duration-300" style={{ width: `${pct}%` }} /></div>
                    {job.total > 0 && <p className="text-xs text-zinc-500 font-mono-data">{Number(job.current || 0).toLocaleString()} / {Number(job.total || 0).toLocaleString()} items</p>}
                  </div>
                )}
                <button data-testid="admin-pl-save" disabled={busy} onClick={add} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-semibold">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save playlist"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function CatchupDebugModal({ mac, pid, onClose }) {
  const [data, setData] = useState(null);
  const [onlyArch, setOnlyArch] = useState(false);
  const [search, setSearch] = useState("");
  const inp = "px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50";

  const load = async () => {
    setData(null);
    const { data } = await api.get(`/admin/devices/${mac}/catchup-debug`, { params: { pid, only_archive: onlyArch || undefined, search: search || undefined } });
    setData(data);
  };
  useEffect(() => { load(); }, [onlyArch]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-2xl p-7 space-y-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="catchup-debug-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5"><div className="p-2 rounded-lg bg-amber-600"><History className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-head text-lg font-semibold">Catch-Up diagnostics</h3><code className="text-xs text-zinc-500 font-mono-data">{mac}</code></div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-zinc-800"><X className="w-4 h-4" /></button>
        </div>

        {!data ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-amber-500 animate-spin" /></div> : (
          <>
            <div className="grid grid-cols-3 gap-3 text-center" data-testid="catchup-summary">
              <div className="p-3 rounded-lg bg-zinc-900"><p className="text-2xl font-bold">{data.summary.total_live?.toLocaleString?.() || 0}</p><p className="text-xs text-zinc-500">Live channels</p></div>
              <div className="p-3 rounded-lg bg-zinc-900"><p className="text-2xl font-bold text-amber-400">{data.summary.with_archive?.toLocaleString?.() || 0}</p><p className="text-xs text-zinc-500">With Catch-Up</p></div>
              <div className="p-3 rounded-lg bg-zinc-900"><p className="text-sm font-semibold mt-1.5 uppercase">{data.summary.playlist_type || "—"}</p><p className="text-xs text-zinc-500 truncate">{data.summary.playlist_name || "—"}</p></div>
            </div>
            {data.summary.with_archive === 0 && (
              <p className="text-xs text-amber-300/80 bg-amber-950/40 border border-amber-900/40 rounded-md px-3 py-2" data-testid="catchup-none-note">
                This provider does not report Catch-Up/archive on any channel — so no CATCH UP badge will appear on TV. This is expected and not a bug.
              </p>
            )}
            <div className="flex gap-2">
              <input data-testid="catchup-search" className={`${inp} flex-1`} placeholder="Search channel…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
              <button data-testid="catchup-only-archive" onClick={() => setOnlyArch((v) => !v)} className={`px-3 py-2 rounded-md text-xs font-semibold ${onlyArch ? "bg-amber-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>Only Catch-Up</button>
            </div>
            <div className="flex-1 overflow-y-auto -mx-1 px-1">
              <table className="w-full text-xs">
                <thead className="text-zinc-500 text-left sticky top-0 bg-[#121217]">
                  <tr><th className="py-1.5">Channel</th><th>Catch-Up</th><th>Days</th><th>Raw flag</th></tr>
                </thead>
                <tbody>
                  {data.channels.map((c, i) => (
                    <tr key={i} data-testid={`catchup-row-${i}`} className="border-t border-zinc-800/50">
                      <td className="py-1.5 pr-2"><span className="truncate block max-w-[240px]">{c.name}</span><span className="text-zinc-600">{c.category}</span></td>
                      <td className={c.catchup ? "text-emerald-400 font-semibold" : "text-zinc-600"}>{c.catchup ? "Yes" : "No"}</td>
                      <td className="text-zinc-400">{c.archive_days || 0}</td>
                      <td className="text-zinc-500 font-mono-data break-all max-w-[180px]">{JSON.stringify(c.archive_raw || {})}</td>
                    </tr>
                  ))}
                  {data.channels.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-zinc-600">No channels match.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function ChangePasswordModal({ onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed successfully");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error changing password");
    } finally { setBusy(false); }
  };

  const input = "w-full px-4 py-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-red-500/50";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#121217] border border-white/10 rounded-2xl w-full max-w-sm p-7 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-red-600"><KeyRound className="w-5 h-5 text-white" /></div>
            <h3 className="font-head text-lg font-semibold">Change password</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-zinc-800 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <input data-testid="current-password-input" className={input} type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} />
        <input data-testid="new-password-input" className={input} type="password" placeholder="New password (min. 6 chars)" value={next} onChange={(e) => setNext(e.target.value)} />
        <input data-testid="confirm-password-input" className={input} type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        <button data-testid="save-password-btn" onClick={submit} disabled={busy}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-semibold transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save"}
        </button>
      </div>
    </div>
  );
}
